import { MepInsightsService } from "../../../../../../../../services/mep/mep-insights.service";
import { MepInsightsRequest, IMepInsightsLongTermForecast } from "../../../../../../../../models/mep/mep-insights.models";
import { ChartLegendViewModel, ChartLegendItem } from "../../../../common-vm/chart-legend-view-model";
import { LineChartHelper } from "../../../../common-vm/line-chart-helper";
import { ConditionalDecimalPlacesValueConverter } from "../../../../converters/conditional-decimal-places-value-converter";
import * as Enumerable from "linq";
import { MepEventMessages, MepTabsEnum } from "../../../../../../../../models/mep/mep.models";
import { EventAggregator } from "aurelia-event-aggregator";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { useView } from "aurelia-framework";
const am4core = await import("@amcharts/amcharts4/core");
const am4charts = await import("@amcharts/amcharts4/charts");
const am4themes_animated = (await import("@amcharts/amcharts4/themes/animated")).default;

@useView('views/pages/modules/mep/tabs/mep-insights/partials/mep-insights-long-term-forecast/mep-insights-long-term-forecast.html')
export class MepInsightsLongTermForecast {
    isLongTermForecastLoading: boolean = false;
    longTermForecastResults: IMepInsightsLongTermForecast[];
    longTermForecastChartViewModel: MepInsightsLongTermForecastChartViewModel;
    isMepTrial: boolean = false;

    constructor(private mepInsightsService: MepInsightsService, private eventAggregator: EventAggregator, isMepTrial: boolean, private configService: ConfigService) {
        this.isMepTrial = isMepTrial;
    }

    getLongTermForecast(request: MepInsightsRequest): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this.isLongTermForecastLoading = true;
            this.mepInsightsService.getMepInsightsLongTermForecast(request).then(response => {
                const co2TypeId: number = 1;

                this.longTermForecastResults = response.results;
                const showIMO = request.emissionTypeId == co2TypeId;
                this.longTermForecastChartViewModel = new MepInsightsLongTermForecastChartViewModel(this.longTermForecastResults, showIMO, this.configService);
                this.isLongTermForecastLoading = false;
                resolve(null);
            }).catch(() => {
                this.isLongTermForecastLoading = false;
                reject("MEP Insights Long Term Forecast - Error retrieving the long term forecast!");
            });
        });
    }

    clearLongTermForecastData() {
        this.longTermForecastResults = null;
        this.longTermForecastChartViewModel = null;
    }

    navigateToEmissionTargets() {
        this.eventAggregator.publish(MepEventMessages.ChangeMepTab, {
            step: MepTabsEnum.EmissionTargets
        });
    }
}

export class MepInsightsLongTermForecastChartViewModel {
    chartData: any;
    legend: ChartLegendViewModel;
    scientificNotationConverter: ConditionalDecimalPlacesValueConverter = new ConditionalDecimalPlacesValueConverter();
    longTermForcastColors: ChartLegendItem[] = [{ id: 1, name: "Forecast", color: "#1A8574" },
    { id: 2, name: "Long-term target", color: "#003745" },
    { id: 3, name: "IMO", color: "#006FF8" },
    { id: 4, name: "Actual", color: "#FC6B37" }]

    constructor(private items: IMepInsightsLongTermForecast[], private showIMO: boolean, private configService: ConfigService) {
        this.initChart();
    }

    initChart(chartDivName: string = "chartdiv2") {
        if (!this.items)
            return;

        const actualName = "Actual";
        const forecastName = "Forecast";
        const longTermTargetName = "Long-term target";
        const imoName = "IMO";
        // here we re-arrange the data to a model that looks like the below 

        /*
        [
            {
                year:'2021'
                Actual: 21213,
                Forecast: 21234,
                Long-term target: 21234,
                IMO: 21234
            }
        ]
        */

        const unifiedEmissionsData = [];

        this.items.forEach(item => {
            if (item.forecastEmissionsTotal != null) {
                unifiedEmissionsData.push({
                    year: item.year,
                    value: item.forecastEmissionsTotal,
                    name: forecastName,
                    id: 1
                });
            }

            if (item.longTermTargetEmissionsTotal != null)
                unifiedEmissionsData.push({
                    year: item.year,
                    value: Math.round(item.longTermTargetEmissionsTotal),
                    name: longTermTargetName,
                    id: 2
                });

            if (item.imoTargetEmissionsTotal != null && this.showIMO) {
                unifiedEmissionsData.push({
                    year: item.year,
                    value: item.imoTargetEmissionsTotal,
                    name: imoName,
                    id: 3
                });
            }

            if (item.actualEmissionsTotal != null) {
                unifiedEmissionsData.push({
                    year: item.year,
                    value: item.actualEmissionsTotal,
                    name: actualName,
                    id: 4
                });
            }
        });

        if (unifiedEmissionsData.length == 0)
            return;

        const groupedData = Enumerable.from(unifiedEmissionsData).groupBy(x => x.year)
            .select(x => {
                const item: any = {};
                item.year = x.first().year;
                x.forEach(y => {
                    if (y.name == actualName) {
                        item[actualName] = this.scientificNotationConverter.toView(y.value, null);
                    }
                    else if (y.name == forecastName) {
                        item[forecastName] = this.scientificNotationConverter.toView(y.value, null);
                    }
                    else if (y.name == longTermTargetName) {
                        item[longTermTargetName] = this.scientificNotationConverter.toView(y.value, null);
                    }
                    else if (this.showIMO) {
                        item[imoName] = this.scientificNotationConverter.toView(y.value, null);
                    }
                });
                return item;
            }).orderBy(x => x.year).toArray();

        //Create chart instance 
        am4core.useTheme(am4themes_animated);
        am4core.addLicense(this.configService.config.settings.amChartsLicense);
        const chart = am4core.create(chartDivName, am4charts.XYChart);

        // Create category axes that will use the year property we created in dataForChart
        const categoryAxis = LineChartHelper.AddXCategoryAxis(chart, "year");
        LineChartHelper.SetXCategoryAxisAsNumeric(categoryAxis);
        LineChartHelper.AddYValueAxis(chart);
        categoryAxis.renderer.minGridDistance = 40;

        const dataItems = Enumerable.from(unifiedEmissionsData).groupBy(x => x.name);
        const legendData = dataItems.select(x => x.first().id)
            .distinct()
            .toArray();

        const isForecastAvailable = groupedData[groupedData.length - 1][forecastName];
        this.legend = new ChartLegendViewModel();
        this.legend.legendItems = this.longTermForcastColors.filter(x => legendData.indexOf(x.id) >= 0 && (x.name != forecastName || isForecastAvailable));

        //We want to draw a line Actual and Target and the dots would then be the months thus we create a line series for each one
        dataItems.forEach(item => {
            const color = this.longTermForcastColors.filter(x => x.name == item.first().name)[0].color;
            const series = LineChartHelper.AddCategorySeries(chart, color, "year", item.key(), '{categoryX} ' + item.key() + ': {' + item.key() + '}');
            const id = item.first().id;
            series.zIndex = id;
            series.bulletsContainer.zIndex = id;
            if (id == 1) {
                series.strokeDasharray = "4,4";
            }
        });

        // chart.tooltip.background.fill = am4core.color("#F0F");

        //use XY cursor to highlight all points 
        chart.cursor = new am4charts.XYCursor();

        const fromYear = Enumerable.from(unifiedEmissionsData).select(x => x.year).min();
        const toYear = Enumerable.from(unifiedEmissionsData).select(x => x.year).max();
        const dataForChart = Enumerable.range(fromYear, toYear - fromYear + 1)
            .select(x => {
                const yearData = Enumerable.from(groupedData).where(y => y.year == x).firstOrDefault();
                return yearData != null ? yearData : {
                    "year": x
                }
            }).toArray();

        chart.data = dataForChart;
    }

    getLegendTooltipText(legendName) {
        const forecastName = "Forecast";
        const longTermTargetName = "Long-term target";
        const imoName = "IMO";

        if (legendName == forecastName) {
            return "Forecast is based on percentage change of previous years.";
        }
        else if (legendName == longTermTargetName) {
            return "Long-term target is based on long-term emission target defined by user in <a class='link' click.delegate='navigateToEmissionTargets()'>Emissions Targets page</a>";
        }
        else if (legendName == imoName) {
            return "IMO decarbonization strategy reference line - 100% reduction of emissions by 2050.";
        }
    }

    getLongTermForcastColors(name: string) {
        return this.longTermForcastColors.filter(x => x.name == name)[0].color;
    }
}